import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import {
  RiShieldCheckLine,
  RiUserHeartLine,
  RiHandHeartLine,
  RiAlertLine,
  RiCheckLine,
  RiArrowRightLine,
  RiMailLine,
  RiFlagLine,
  RiHeartLine,
  RiVolumeUpLine,
  RiTeamLine,
  RiForbidLine,
  RiMailSendLine,
  RiEarthLine,
  RiStarFill,
  RiShieldStarLine,
  RiGroupLine,
  RiAwardLine,
  RiSendPlaneLine,
  RiCheckboxCircleLine,
  RiExternalLinkLine,
  RiInformationLine,
  RiLockLine,
  RiGlobalLine,
  RiMicroscopeLine,
  RiArrowUpLine,
} from 'react-icons/ri';

/* ─── THEME COLORS (Teal & Gold Palette matching About page) ───────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
  gradPurple: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)",
  gradOrange: "linear-gradient(135deg, #F59E0B 0%, #F97316 100%)",
  gradRed: "linear-gradient(135deg, #EF4444 0%, #F97316 100%)",
  success: "#2A9D8F",
  warning: "#D4A017",
  danger: "#218577",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  @media (max-width: 768px) {
    section { padding: 48px 24px !important; }
  }
`;

/* ─── CONDUCT SECTIONS ────────────────────────────────────────────────────── */
const sections = [
  {
    icon: RiShieldCheckLine,
    number: '01',
    title: 'Safe & Inclusive Environment',
    subtitle: 'Zero Tolerance for Discrimination',
    content: 'Meetings21 is dedicated to providing an environment free from discrimination, bullying, harassment or victimization, where all attendees are treated with respect and dignity — aiming to create a culture of diversity where all are valued for their contribution and individuality. This code applies to all attendees both in person or remotely.',
    color: T.primary,
    gradient: T.gradPrimary,
    highlights: ['No discrimination of any kind', 'Dignity and respect for all', 'Applies in-person and remotely'],
  },
  {
    icon: RiVolumeUpLine,
    number: '02',
    title: 'Professional Communication',
    subtitle: 'Appropriate Language & Imagery',
    content: 'All communications (both verbal and written) should be appropriate for a professional audience that includes a diverse range of backgrounds. Sexist or inappropriate language and imagery should not be used at any time during meetings, conferences, and events — including presentations and posters.',
    color: T.accent,
    gradient: T.gradAccent,
    highlights: ['Professional language required', 'No inappropriate imagery', 'Committee may withdraw content'],
  },
  {
    icon: RiUserHeartLine,
    number: '03',
    title: 'Respectful Behavior',
    subtitle: 'Kindness & Professionalism',
    content: 'Be kind to others and treat each other with respect. Do not insult or put down other attendees. Behave professionally. Remember that harassment and sexist, racist, or exclusionary jokes are not appropriate for any summit-related event.',
    color: '#8B5CF6',
    gradient: T.gradPurple,
    highlights: ['Treat others with kindness', 'No harassment of any form', 'No exclusionary language'],
  },
  {
    icon: RiForbidLine,
    number: '04',
    title: 'Consequences of Violation',
    subtitle: 'Immediate Action & Expulsion',
    content: 'Individuals asked to stop any inappropriate behavior are expected to comply immediately. Any attendees violating this code, after due consideration, may be asked to leave the event at the sole discretion of the Organizing Committee and Management of Meetings21 without refund.',
    color: T.danger,
    gradient: T.gradRed,
    highlights: ['Immediate compliance required', 'Removal without refund possible', 'May be reported to institution'],
  },
  {
    icon: RiMailSendLine,
    number: '05',
    title: 'Reporting Mechanism',
    subtitle: 'Confidential & Supportive',
    content: 'Any breach of this code should be brought to the attention of the Meetings21 organizers at the event, who will inform the Committee and Management with the person\'s agreement. The Organizing Committee will contact the people involved and try to reach a resolution.',
    color: T.success,
    gradient: T.gradSuccess,
    highlights: ['Confidential reporting available', 'Dedicated support contacts', 'Fair resolution process'],
  },
  {
    icon: RiGlobalLine,
    number: '06',
    title: 'Scope of Enforcement',
    subtitle: 'All Event Spaces & Platforms',
    content: 'This Code of Conduct applies to all attendees both in person or remotely, including all meeting rooms, social functions, online platforms, and any other spaces where summit participants are present. The committee reserves the right to withdraw talks, posters, and social media posts if they contravene this code.',
    color: T.warning,
    gradient: T.gradOrange,
    highlights: ['All venues and platforms covered', 'Social events included', 'Online participation covered'],
  },
];

/* ─── COMMITMENTS ─────────────────────────────────────────────────────────── */
const commitments = [
  { icon: RiShieldStarLine, label: 'Safety First' },
  { icon: RiGroupLine, label: 'Inclusivity' },
  { icon: RiHeartLine, label: 'Respect' },
  { icon: RiLockLine, label: 'Confidentiality' },
  { icon: RiAwardLine, label: 'Excellence' },
  { icon: RiEarthLine, label: 'Global Reach' },
];

/* ─── STATS ───────────────────────────────────────────────────────────────── */
const stats = [
  { value: '100%', label: 'Zero Tolerance', sub: 'For harassment & discrimination' },
  { value: '24/7', label: 'Support Available', sub: 'During all event hours' },
  { value: '60+', label: 'Countries', sub: 'Protected under this code' },
  { value: '10+', label: 'Years', sub: 'Upholding these standards' },
];

/* ─── SECTION CARD ────────────────────────────────────────────────────────── */
function SectionCard({ section, index }) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      style={{ width: '100%' }}
    >
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: T.white,
          borderRadius: 28,
          border: hovered ? `1px solid ${section.color}30` : `1px solid ${T.border}`,
          boxShadow: hovered ? `0 30px 45px -12px ${section.color}15, 0 1px 3px rgba(0,0,0,0.05)` : T.shadowMd,
          transition: 'all 0.35s cubic-bezier(0.21,1.11,0.35,1)',
          transform: hovered ? 'translateY(-5px)' : 'translateY(0)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: hovered ? 5 : 3,
          background: section.gradient,
          transition: 'width 0.3s ease',
          borderRadius: '28px 0 0 28px',
        }} />

        <div style={{
          position: 'absolute',
          top: -20,
          right: 32,
          fontSize: 140,
          fontWeight: 900,
          fontFamily: "'Playfair Display', serif",
          color: `${section.color}06`,
          lineHeight: 1,
          pointerEvents: 'none',
          userSelect: 'none',
        }}>
          {section.number}
        </div>

        <div style={{ padding: '40px 48px 40px 60px' }}>
          <div style={{ display: 'flex', gap: 36, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flexShrink: 0 }}>
              <div style={{
                width: 76,
                height: 76,
                borderRadius: 24,
                background: section.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: hovered ? `0 12px 28px ${section.color}30` : `0 4px 12px ${section.color}10`,
                transition: 'box-shadow 0.3s',
              }}>
                <section.icon size={36} color={T.white} />
              </div>
              <div style={{
                textAlign: 'center',
                marginTop: 12,
                fontFamily: "'Playfair Display', serif",
                fontSize: 13,
                fontWeight: 700,
                color: section.color,
                opacity: 0.7,
                letterSpacing: '0.05em',
              }}>
                {section.number}
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: `${section.color}10`,
                border: `1px solid ${section.color}20`,
                padding: '6px 16px',
                borderRadius: 30,
                marginBottom: 14,
              }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: section.color }} />
                <span style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: section.color,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}>{section.subtitle}</span>
              </div>

              <h3 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 26,
                fontWeight: 800,
                color: T.text,
                marginBottom: 16,
                lineHeight: 1.2,
              }}>{section.title}</h3>

              <p style={{
                fontSize: 16,
                lineHeight: 1.75,
                color: T.textMid,
                marginBottom: 24,
              }}>{section.content}</p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {section.highlights.map((h, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: `${section.color}08`,
                    border: `1px solid ${section.color}15`,
                    padding: '7px 14px',
                    borderRadius: 20,
                  }}>
                    <RiCheckLine size={13} color={section.color} />
                    <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{h}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── STAT CARD ───────────────────────────────────────────────────────────── */
function StatCard({ stat, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{
        textAlign: 'center',
        padding: '32px 20px',
        background: T.white,
        borderRadius: 24,
        border: `1px solid ${T.border}`,
        boxShadow: T.shadowMd,
        flex: 1,
        minWidth: 160,
      }}
    >
      <div style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 42,
        fontWeight: 900,
        background: T.gradPrimary,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        marginBottom: 8,
      }}>{stat.value}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>{stat.label}</div>
      <div style={{ fontSize: 12, color: T.textMuted }}>{stat.sub}</div>
    </motion.div>
  );
}

/* ─── MAIN EXPORT ─────────────────────────────────────────────────────────── */
export default function CodeOfConduct() {
  const [showBtt, setShowBtt] = useState(false);
  const location = useLocation();
  
  // Get eventId from URL query params instead of using useSummit hook
  const getEventIdFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('eventId') || null;
  };
  
  const eventId = getEventIdFromUrl();

  useEffect(() => {
    const fn = () => setShowBtt(window.scrollY > 500);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <>
      <style>{css}</style>
      
      <div style={{ minHeight: '100vh', background: T.offWhite, fontFamily: "'Inter', sans-serif" }}>
        
        {/* ── HERO SECTION (Matching About page) ────────────────────────────────────── */}
        <section style={{ padding: 'clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px)', background: T.gradSoft, borderBottom: `1px solid ${T.border}`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <span style={{ 
                padding: '5px 14px', 
                borderRadius: 20, 
                background: T.softTeal, 
                border: `1px solid ${T.border}`, 
                color: T.primary, 
                fontSize: 11, 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                letterSpacing: '0.1em' 
              }}>
                <RiShieldCheckLine size={13} style={{ display: 'inline', marginRight: 6 }} />
                Code of Conduct
              </span>
            </div>
            <h1 style={{ 
              fontFamily: "'Playfair Display', serif", 
              fontSize: 'clamp(2rem, 4.5vw, 3.4rem)', 
              fontWeight: 900, 
              color: T.navy, 
              letterSpacing: '-0.03em', 
              lineHeight: 1.06, 
              marginBottom: 14 
            }}>
              Our <em style={{ color: T.accentGold, fontStyle: 'normal' }}>Commitment</em>
            </h1>
            <p style={{ 
              fontSize: 'clamp(0.88rem, 1.3vw, 1rem)', 
              color: T.textMid, 
              lineHeight: 1.7, 
              maxWidth: 560 
            }}>
              Creating a respectful, safe, and inclusive environment for every participant — 
              whether attending in person or joining us remotely from across the globe.
            </p>
            
            {/* Decorative line */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
        </section>

        {/* ── STATS BAR ─────────────────────────────────────────────────────── */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '24px 40px 0' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
            <div style={{ background: T.white, borderRadius: 12, padding: '16px 24px', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 120 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.primary }}>100%</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Zero Tolerance</div>
            </div>
            <div style={{ background: T.white, borderRadius: 12, padding: '16px 24px', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 120 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.accentGold }}>24/7</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Support Available</div>
            </div>
            <div style={{ background: T.white, borderRadius: 12, padding: '16px 24px', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 120 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.primary }}>60+</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Countries</div>
            </div>
          </div>
        </div>

        {/* ── COMMITMENT PILLS ──────────────────────────────────────────────── */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '20px 40px 0' }}>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 40 }}>
            {commitments.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.35 + i * 0.05 }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: T.white, border: `1px solid ${T.border}`,
                  padding: '10px 20px', borderRadius: 40,
                  boxShadow: T.shadow,
                }}
              >
                <c.icon size={16} style={{ color: T.primary }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{c.label}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── MAIN CONTENT ──────────────────────────────────────────────────── */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 40px 80px' }}>
          
          {/* Why This Matters Banner */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{
              background: T.white,
              borderRadius: 32,
              border: `1px solid ${T.border}`,
              padding: '48px 56px',
              display: 'flex',
              alignItems: 'center',
              gap: 48,
              flexWrap: 'wrap',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: T.shadowLg,
              marginBottom: 60,
            }}
          >
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 4,
              background: T.gradPrimary,
            }} />
            <div style={{
              width: 80, height: 80, borderRadius: 24,
              background: T.gradPrimary,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              boxShadow: T.shadow,
            }}>
              <RiInformationLine size={40} color={T.white} />
            </div>
            <div style={{ flex: 1, minWidth: 260 }}>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 28, fontWeight: 800, color: T.text, marginBottom: 12,
              }}>Why This Matters</h2>
              <p style={{ fontSize: 16, color: T.textMid, lineHeight: 1.7 }}>
                Academic conferences thrive when every participant feels safe, valued, and heard. 
                This Code of Conduct is not merely a set of rules — it is our collective promise 
                to uphold the standards that make meaningful scholarly exchange possible.
              </p>
            </div>
          </motion.div>

          {/* Six Pillars Section */}
          <div style={{ marginBottom: 60 }}>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(36px, 5vw, 52px)',
                fontWeight: 800, color: T.text, marginBottom: 14,
              }}>
                The{' '}
                <span style={{ background: T.gradPrimary, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Six Pillars
                </span>
              </h2>
              <p style={{ fontSize: 17, color: T.textMuted, maxWidth: 580, margin: '0 auto' }}>
                Our code of conduct is built on six foundational principles that guide every interaction at Meetings21
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
              {sections.map((s, i) => (
                <SectionCard key={i} section={s} index={i} />
              ))}
            </div>
          </div>

          {/* Quick Reference Grid */}
          <div style={{ marginBottom: 60 }}>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(36px, 5vw, 52px)',
                fontWeight: 800, color: T.text, marginBottom: 14,
              }}>Quick Reference</h2>
              <p style={{ fontSize: 17, color: T.textMuted, maxWidth: 580, margin: '0 auto' }}>
                A snapshot of what is and isn't acceptable at Meetings21 events
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
              {/* DO column */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                style={{
                  background: T.white,
                  borderRadius: 28,
                  border: `1px solid ${T.border}`,
                  padding: '36px 40px',
                  boxShadow: T.shadowMd,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: T.gradSuccess }} />
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28,
                }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: 16,
                    background: T.gradSuccess,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <RiCheckboxCircleLine size={28} color={T.white} />
                  </div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800, color: T.success }}>
                    Expected Behavior
                  </h3>
                </div>
                {[
                  'Treat all participants with respect and dignity',
                  'Use inclusive and professional language',
                  'Welcome diverse perspectives and backgrounds',
                  'Report concerns through proper channels',
                  'Participate constructively in discussions',
                  'Respect others\' personal boundaries',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                    <RiCheckLine size={16} color={T.success} style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={{ fontSize: 15, color: T.textMid, lineHeight: 1.5 }}>{item}</span>
                  </div>
                ))}
              </motion.div>

              {/* DON'T column */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                style={{
                  background: T.white,
                  borderRadius: 28,
                  border: `1px solid ${T.border}`,
                  padding: '36px 40px',
                  boxShadow: T.shadowMd,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: T.gradRed }} />
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28,
                }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: 16,
                    background: T.gradRed,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <RiForbidLine size={28} color={T.white} />
                  </div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800, color: T.danger }}>
                    Prohibited Conduct
                  </h3>
                </div>
                {[
                  'Harassment, bullying, or intimidation of any kind',
                  'Discriminatory language, jokes, or imagery',
                  'Sexist, racist, or exclusionary remarks',
                  'Unauthorized photography or recording',
                  'Disruption of presentations or discussions',
                  'Inappropriate physical contact',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                    <RiAlertLine size={16} color={T.danger} style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={{ fontSize: 15, color: T.textMid, lineHeight: 1.5 }}>{item}</span>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>

          {/* Reporting Process */}
          <div style={{ marginBottom: 60 }}>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(36px, 5vw, 52px)',
                fontWeight: 800, color: T.text, marginBottom: 14,
              }}>How to Report</h2>
              <p style={{ fontSize: 17, color: T.textMuted, maxWidth: 580, margin: '0 auto' }}>
                We take all reports seriously. Here's what happens when you reach out to us.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
              {[
                { step: '01', icon: RiMailLine, title: 'Contact Us', desc: 'Reach out to an organizer at the event or email our conduct team anytime.' },
                { step: '02', icon: RiShieldCheckLine, title: 'Confidential Review', desc: 'Your report is handled with full confidentiality. Two committee members are assigned to your case.' },
                { step: '03', icon: RiTeamLine, title: 'Resolution Process', desc: 'We contact all involved parties and work toward a fair, appropriate resolution based on the situation.' },
                { step: '04', icon: RiCheckboxCircleLine, title: 'Follow-Up', desc: 'We follow up to ensure the resolution was effective and that you feel safe continuing to participate.' },
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    background: T.white,
                    borderRadius: 24,
                    border: `1px solid ${T.border}`,
                    padding: '32px 28px',
                    boxShadow: T.shadowMd,
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: -16, right: -8,
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 100, fontWeight: 900,
                    color: `${T.primary}06`, lineHeight: 1,
                    pointerEvents: 'none',
                  }}>{step.step}</div>
                  <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: T.gradPrimary,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 20px',
                    boxShadow: T.shadow,
                  }}>
                    <step.icon size={30} color={T.white} />
                  </div>
                  <div style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 11, fontWeight: 700, color: T.primary,
                    letterSpacing: '0.1em', marginBottom: 10,
                  }}>STEP {step.step}</div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 12 }}>{step.title}</h3>
                  <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.65 }}>{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Contact Box - Gold themed */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{
              background: T.gradNavy,
              borderRadius: 36,
              padding: '64px 56px',
              textAlign: 'center',
              boxShadow: T.shadowLg,
              position: 'relative',
              overflow: 'hidden',
              marginBottom: 48,
            }}
          >
            <div style={{
              position: 'absolute', width: 300, height: 300,
              border: '1px solid rgba(255,255,255,0.1)', borderRadius: '50%',
              top: -100, right: -80, pointerEvents: 'none',
            }} />
            <div style={{
              position: 'absolute', width: 200, height: 200,
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: '50%',
              bottom: -60, left: -40, pointerEvents: 'none',
            }} />

            <div style={{
              width: 80, height: 80, borderRadius: '50%',
              background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 28px',
              backdropFilter: 'blur(10px)',
            }}>
              <RiMailLine size={40} color={T.white} />
            </div>

            <h3 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 38, fontWeight: 900, color: T.white, marginBottom: 16,
            }}>Report a Concern</h3>
            <p style={{
              fontSize: 18, color: 'rgba(255,255,255,0.85)',
              marginBottom: 36, lineHeight: 1.6,
              maxWidth: 560, marginLeft: 'auto', marginRight: 'auto',
            }}>
              If you experience or witness any violation of this code of conduct, 
              please contact our team immediately. All reports are taken seriously 
              and handled with complete confidentiality.
            </p>

            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
             
            </div>
          </motion.div>

          {/* Scope Note */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            style={{
              background: T.white,
              borderRadius: 24,
              border: `1px solid ${T.border}`,
              padding: '32px 40px',
              display: 'flex',
              gap: 20,
              alignItems: 'flex-start',
              boxShadow: T.shadowMd,
            }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: 14,
              background: `${T.warning}15`, border: `1px solid ${T.warning}25`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <RiAlertLine size={22} color={T.warning} />
            </div>
            <div>
              <h4 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8 }}>Scope of Application</h4>
              <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.7 }}>
                This Code of Conduct applies to all attendees — in person or remotely — including all meeting rooms, 
                social functions, online platforms, networking events, and any other spaces where Meetings21 
                participants are present. The Organizing Committee and Management reserve the right to withdraw 
                talks, posters, and social media posts if they contravene this code.
              </p>
            </div>
          </motion.div>
        </div>

        {/* ── CTA SECTION (Matching Topics) ──────────────────────────────────── */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 40px 80px' }}>
          <div style={{
            background: T.gradNavy,
            borderRadius: 24,
            padding: '48px 56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 32,
            textAlign: 'center',
          }}>
            <div>
              <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 20, background: 'rgba(59,188,174,0.15)', border: '1px solid rgba(59,188,174,0.25)', color: T.accent, fontSize: 11, fontWeight: 700, marginBottom: 12 }}>
                Join Us
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.4rem, 2.5vw, 2rem)', fontWeight: 900, color: T.white, marginBottom: 8 }}>
                Join a Respectful Community
              </h2>
              <p style={{ fontSize: 14, color: T.textMuted }}>Be part of an academic community where every scholar is respected.</p>
            </div>
            <div>
              <Link to="/events" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 28px',
                background: T.gradGold,
                color: T.navyDeep,
                borderRadius: 50,
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: 700,
                transition: 'all 0.25s',
                boxShadow: T.shadowGold,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = T.shadowLg; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = T.shadowGold; }}>
                Register Now
                <RiArrowRightLine size={15} />
              </Link>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 10, textAlign: 'center' }}>By registering, you agree to our Code of Conduct</div>
            </div>
          </div>
        </div>

        {/* BACK TO TOP - Gold themed */}
        {showBtt && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              position: 'fixed',
              bottom: 26,
              right: 26,
              width: 42,
              height: 42,
              borderRadius: 10,
              background: T.gradGold,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: T.navyDeep,
              boxShadow: T.shadowGold,
              zIndex: 100,
            }}
          >
            <RiArrowUpLine size={18} />
          </button>
        )}
      </div>
    </>
  );
}